import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Dropdown, Modal } from 'react-bootstrap'
import Select from 'react-select'

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import useFilterSearchPagination from 'pages/parts/useFilterSearchPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'

import { formatMoney, formatDate, returnSign } from "helpers/helpers"
import { httpOk, GET, PUT } from 'helpers/api'
import { hasPermission } from 'helpers/permissions'

function Index() {
	const { t } = useTranslation()
	const date = new Date()

	const [data, setData] = useState({})
	const [modalInfo, setModalInfo] = useState(false)
	const [modalPaymentType, setModalPaymentType] = useState(false)
	const [selectedPayment, setSelectedPayment] = useState({
		id: null,
		pos_payment_type_id: null,
		payment_type_name: '',
		pos_name: '',
		login: '',
	})
	const [payments, setPayments] = useState([])
	const [paymentTypes, setPaymentTypes] = useState([])
	// Руками ставятся только наличка, карта и расчётный счёт: типы платёжных систем
	// приходят от самих систем, за бонусными стоят списания с бонусных балансов.
	const manualPaymentTypes = paymentTypes.filter(type => type.is_manual)
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const {
		filterData,
		setFilterData,
		filterDataExist,
		setFilterDataExist,
		pagination,
		setPagination,
		applySearch,
		applyFilters,
		paginate,
	} = useFilterSearchPagination({
		initialFilterData: {
			start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
			end_date: formatDate(new Date(), 'yyyy-MM-dd'),
			search: '',
			page: null,
		},
		initialPagination: {
			url: '/partner/transactions',
			response: null
		},
	})

	async function searchWithFilter(nextFilterData = filterData) {
		var filterDataCopy = { ...nextFilterData }
		var filterUrl = "/admin/payment-history"
		var urlParams = ""
		checkFilter(filterDataCopy)

		if (filterDataCopy.search)
			urlParams += returnSign(urlParams) + 'search=' + filterDataCopy.search
		if (filterDataCopy.pos_payment_type_id)
			urlParams += returnSign(urlParams) + 'pos_payment_type_id=' + filterDataCopy.pos_payment_type_id
		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
		if (filterDataCopy.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date
		if (filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		if (`/admin/payment-history${urlParams}` !== filterUrl)
			setFilterDataExist(true)

		filterUrl += urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'response': response.data })
		}

		setDropdownIsOpen(false)
	}

	async function toggleModalInfo(bool) {
		if (bool) {
			const response = await GET('/admin/payment-history-group', filterData, { loader: true })
			if (httpOk(response)) setPayments(response.data)
		}

		setModalInfo(bool)
	}

	function checkFilter(currentFilterData) {
		if (!currentFilterData.search && !currentFilterData.page) {
			setFilterDataExist(false)
		}
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	function handlePaginate(page) {
		paginate(page)
		searchWithFilter({ ...filterData, page })
	}

	async function getPaymentTypes() {
		const response = await GET('/admin/pos-payment-types')
		if (httpOk(response)) setPaymentTypes(response.data)
	}

	/**
	 * Менять тип можно только у платежей, заведённых вручную: поступление от payme
	 * или rahmat — факт, пришедший от платёжной системы. За бонусными типами стоят
	 * списания с бонусных балансов, их тоже не переименовываем.
	 */
	function canChangePaymentType(item) {
		if (!hasPermission('admin_payments_history_update')) return false
		if (item?.payment_type !== 'admin') return false
		if (item?.type_is_manual === false) return false
		return true
	}

	function openPaymentTypeModal(item) {
		setSelectedPayment({
			id: item.id,
			pos_payment_type_id: item.pos_payment_type_id,
			payment_type_name: item.payment_type_name,
			pos_name: item.pos_name,
			login: item.login,
		})
		setModalPaymentType(true)
	}

	async function savePaymentType() {
		if (!selectedPayment.id || !selectedPayment.pos_payment_type_id) {
			return
		}

		const response = await PUT('/admin/payment-history/payment-type', {
			id: selectedPayment.id,
			pos_payment_type_id: selectedPayment.pos_payment_type_id,
		}, { loader: true })

		if (httpOk(response)) {
			setModalPaymentType(false)
			searchWithFilter()
		}
	}

	useEffect(() => {
		getPaymentTypes()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="vertical-center">
					<h4>{t('payment_history')} [{data.total}]</h4>
				</div>
				<div className="d-flex">
					<button className="btn-rounded btn btn-primary"
						onClick={() => toggleModalInfo(true)}>
						{t('information')}
					</button>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between mb-2">
						<div className="d-flex flex-wrap gap-2">
							<div>
								<FilterSearch
								filterData={filterData}
								onSearchChange={(search) => applySearch(search, (nextFilterData) => {
									searchWithFilter(nextFilterData)
								})}>
								</FilterSearch>
							</div>
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen} className="h-fit-content">
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('payment_types')}</label>
												<Select
													isClearable
													options={paymentTypes}
													value={paymentTypes.find(option => option.id === filterData.pos_payment_type_id)}
													onChange={(option) => setFilterData({ ...filterData, 'pos_payment_type_id': option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('period')}</label>
												<PeriodPicker
													startDate={filterData.start_date}
													endDate={filterData.end_date}
													onChange={(period) => setFilterData({ ...filterData, ...period })}
													withTime={false}
													minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
													maxDate={new Date(new Date().getFullYear(), 11, 31)}
												/>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary"
														onClick={() => setDropdownIsOpen(false)}>
														{t('cancel')}
													</button>
													<button className="btn btn-primary"
														onClick={() => applyFilters((nextFilterData) => {
															searchWithFilter(nextFilterData)
														})}>
														{t('apply')}
													</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
						</div>
						<div className="vertical-center">
							<h5 className="d-flex justify-content-between gap-3">
								<b>{t('amount')}:</b> {formatMoney(data.total_amount)}
							</h5>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date &&
											<span>{formatDate(filterData.start_date)}</span>
										}
										{(filterData.start_date && filterData.end_date) &&
											<b> - </b>
										}
										{filterData.end_date &&
											<span>{formatDate(filterData.end_date)}</span>
										}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '20%' }}>{t('pos')}</th>
									<th style={{ 'width': '15%' }}>{t('login')}</th>
									<th style={{ 'width': '15%' }}>{t('note')}</th>
									<th style={{ 'width': '10%' }}>Кто провёл</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('date')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('type')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('tariff')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('amount')}</th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{item.row_number}</td>
										<td className="td-ellipsis">{item.pos_name}</td>
										<td className="td-ellipsis">{item.login}</td>
										<td className="td-ellipsis" title={item.note}>{item.note}</td>
										<td className="td-ellipsis">{item.created_by}</td>
										<td className="text-center">{formatDate(item.created_at, 'dd.MM.yy HH:mm')}</td>
										<td className="text-center td-ellipsis">
											{canChangePaymentType(item) ?
												<span
													className="text-primary cursor-pointer"
													style={{ textDecoration: 'underline', cursor: 'pointer' }}
													title={t('edit')}
													onClick={() => openPaymentTypeModal(item)}>
													{item.payment_type_name || '—'}
												</span>
												:
												<span>{item.payment_type_name || '—'}</span>
											}
										</td>
										<td className="text-end">{formatMoney(item.tariff)}</td>
										<td className="text-end">{formatMoney(item.amount)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={handlePaginate}>
						</FilterPagination>
					}
				</div>
			</div>

			{/* MODAL PAYMENT HISTORY*/}
			<Modal show={modalInfo} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => setModalInfo(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						История
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th className="text-nowrap">{t('type')}</th>
									<th className="text-nowrap text-end">{t('amount')}</th>
								</tr>
							</thead>
							<tbody>
								{payments?.data?.map((item, index) => (
									<tr key={index}>
										<td className="td-ellipsis" title={item.name}>{item.name}</td>
										<td className="text-nowrap text-end">{formatMoney(item.amount)}</td>
									</tr>
								))}
							</tbody>
							<tfoot>
								<tr>
									<td><b>{t('total')}</b></td>
									<td className="text-nowrap text-end"><b>{formatMoney(payments.total_amount)}</b></td>
								</tr>
							</tfoot>
						</table>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL PAYMENT HISTORY*/}

			{/* MODAL CHANGE PAYMENT TYPE */}
			<Modal show={modalPaymentType} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => setModalPaymentType(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('payment_types')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="mb-2">
						<div><b>{t('pos')}:</b> {selectedPayment.pos_name}</div>
						<div><b>{t('login')}:</b> {selectedPayment.login}</div>
					</div>
					<div className="form-group">
						<label>{t('type')}</label>
						<Select
							options={manualPaymentTypes}
							value={manualPaymentTypes.find(option => option.id === selectedPayment.pos_payment_type_id) || null}
							onChange={(option) => setSelectedPayment({
								...selectedPayment,
								pos_payment_type_id: option ? option.id : null,
							})}
							placeholder=""
							noOptionsMessage={() => t('list_empty')}
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex gap-2 w-100 justify-content-end">
						<button className="btn btn-secondary"
							onClick={() => setModalPaymentType(false)}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary"
							onClick={savePaymentType}>
							{t('save')}
						</button>
					</div>
				</Modal.Footer>
			</Modal>
			{/* MODAL CHANGE PAYMENT TYPE */}
		</>
	)
}

export default Index